import { useState } from "react";
import {
  Phone,
  ArrowRight,
  Sparkles,
  Menu,
  X,
  Zap,
  ShieldCheck,
  ThumbsUp,
  Signal,
} from "lucide-react";

const NAV_ITEMS = [
  { label: "Home", href: "#home" },
  { label: "Features", href: "#features" },
  { label: "Networks", href: "#networks" },
  { label: "How to Pay", href: "#pay" },
  { label: "Contact", href: "#contact" },
];

const NETWORKS = [
  "Safaricom",
  "Airtel",
  "Telkom",
  "JTL",
  "Faiba",
  "Equitel",
];

export default function Hero() {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="home"
      className="hero-bg grain relative overflow-hidden min-h-screen"
      data-testid="hero-section"
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(245,197,24,0.35), transparent 70%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -left-24 h-[480px] w-[480px] rounded-full"
        style={{
          background:
            "radial-gradient(closest-side, rgba(29,184,106,0.35), transparent 70%)",
        }}
      />

      {/* NAV */}
      <header className="relative z-20">
        <nav
          className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex items-center justify-between"
          data-testid="navbar"
        >
          {/* Brand */}
          <a
            href="#home"
            className="flex items-center gap-2.5"
            data-testid="brand-logo"
          >
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ background: "var(--dg-gold)" }}
            >
              <span className="font-display font-extrabold text-[#1a1300]">
                D
              </span>
            </span>
            <span className="font-display font-extrabold text-white text-lg tracking-tight">
              Dolaglobo
            </span>
          </a>

          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <li key={item.label}>
                <a
                  href={item.href}
                  className="nav-link"
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href="#pay"
              className="btn-primary hidden sm:inline-flex !py-2.5 !px-5 text-sm"
              data-testid="nav-pay-now-btn"
            >
              Pay Now
              <ArrowRight size={16} />
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-white/90 hover:bg-white/10"
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {open && (
          <div
            className="lg:hidden mx-5 mb-3 rounded-2xl glass p-4"
            data-testid="mobile-menu"
          >
            <ul className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2.5 rounded-xl text-white/90 hover:bg-white/10 text-sm font-medium"
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#pay"
                  onClick={() => setOpen(false)}
                  className="btn-primary w-full justify-center mt-2 text-sm"
                  data-testid="mobile-pay-now-btn"
                >
                  Pay Now <ArrowRight size={16} />
                </a>
              </li>
            </ul>
          </div>
        )}
      </header>

      {/* HERO CONTENT */}
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-8 lg:pt-14 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
          {/* LEFT */}
          <div>
            <span
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs font-medium text-white fade-up"
              data-testid="hero-badge"
            >
              <Sparkles size={14} className="text-[var(--dg-gold)]" />
              Finance For Growth
            </span>

            <h1
              className="font-display mt-6 text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] fade-up delay-1"
              data-testid="hero-headline"
            >
              Conveniently Buy
              <br />
              Airtime{" "}
              <span style={{ color: "var(--dg-gold)" }}>
                Across All Networks
              </span>
            </h1>

            <p
              className="mt-6 max-w-xl text-white/85 text-base sm:text-lg leading-relaxed fade-up delay-2"
              data-testid="hero-subtext"
            >
              Top up Safaricom, Airtel, Telkom, JTL, Faiba and Equitel in
              seconds using our Mpesa Paybill. Simple. Secure. Instant.
              That&apos;s Dolaglobo.
            </p>

            <div
              className="mt-8 flex flex-wrap items-center gap-3 fade-up delay-3"
              data-testid="hero-cta-row"
            >
              <a href="#pay" className="btn-primary" data-testid="cta-buy-now">
                Buy Airtime Now <ArrowRight size={18} />
              </a>
              <a
                href="#networks"
                className="btn-ghost"
                data-testid="cta-view-networks"
              >
                View Networks
              </a>
            </div>

            <div className="mt-7 fade-up delay-4">
              <div className="paybill-chip" data-testid="paybill-chip">
                <span className="icon">
                  <Phone size={18} />
                </span>
                <div className="leading-tight">
                  <div className="text-[10px] uppercase tracking-[0.18em] opacity-70">
                    Mpesa Paybill
                  </div>
                  <div className="text-lg font-extrabold">4114517</div>
                </div>
              </div>
            </div>

            {/* Networks pills */}
            <div
              className="mt-8 flex flex-wrap gap-2 fade-up delay-4"
              data-testid="networks-list"
            >
              {NETWORKS.map((n) => (
                <span
                  key={n}
                  className="text-[11px] sm:text-xs font-semibold tracking-wide uppercase rounded-full px-3 py-1.5 bg-white/10 text-white/90 border border-white/15"
                  data-testid={`network-${n.toLowerCase()}`}
                >
                  {n}
                </span>
              ))}
            </div>
          </div>

          {/* RIGHT — phone card */}
          <div className="relative fade-up delay-2">
            <PhoneCard />
          </div>
        </div>

        {/* Footer mini */}
        <div
          className="mt-16 lg:mt-24 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-white/70 text-xs"
          data-testid="hero-footnote"
        >
          <p>© {new Date().getFullYear()} Dolaglobo. All rights reserved.</p>
          <p className="opacity-80">
            Top up instantly via Mpesa Paybill{" "}
            <span className="text-white font-semibold">4114517</span>
          </p>
        </div>
      </div>
    </section>
  );
}

function PhoneCard() {
  return (
    <div className="relative mx-auto w-full max-w-md lg:max-w-lg" data-testid="phone-card">
      {/* Floating badges */}
      <div className="absolute -top-4 -left-4 z-10 hidden sm:flex items-center gap-2 rounded-full bg-white px-3.5 py-2 shadow-lg" data-testid="float-badge-instant">
        <Zap size={14} className="text-[var(--dg-green-700)]" />
        <span className="text-xs font-semibold text-[#0b1f15]">Instant Top-Up</span>
      </div>
      <div className="absolute -bottom-4 -right-4 z-10 hidden sm:flex items-center gap-2 rounded-full bg-[var(--dg-gold)] px-3.5 py-2 shadow-lg" data-testid="float-badge-secure">
        <ShieldCheck size={14} className="text-[#1a1300]" />
        <span className="text-xs font-extrabold text-[#1a1300]">100% Secure</span>
      </div>

      <div className="phone-card p-6 sm:p-7">
        {/* Top row */}
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg"
              style={{ background: "var(--dg-gold)" }}
            >
              <span className="font-display font-extrabold text-[#1a1300] text-sm">D</span>
            </span>
            <span className="font-display font-extrabold tracking-tight">Dolaglobo</span>
          </div>
          <Signal size={16} className="text-white/80" />
        </div>

        <div className="mt-5">
          <span className="inline-block rounded-full bg-white/15 border border-white/20 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">
            Finance For Growth
          </span>
        </div>

        <h3 className="font-display mt-4 text-white text-2xl sm:text-[26px] font-extrabold leading-tight">
          Conveniently Buy Airtime Across All Networks
        </h3>

        <p className="mt-3 text-white/85 text-sm">
          Using our <span className="font-semibold text-white">Mpesa Paybill</span>
        </p>
        <div className="mt-1 text-[var(--dg-gold)] font-display font-extrabold text-3xl tracking-tight">
          4114517
        </div>

        {/* Account input */}
        <div className="mt-5 rounded-2xl bg-white/10 border border-white/15 px-4 py-3 flex items-center gap-3">
          <Phone size={16} className="text-[var(--dg-gold)]" />
          <div className="leading-tight">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">
              Account Number
            </div>
            <div className="text-sm text-white/90 font-medium">Phone Number</div>
          </div>
        </div>

        {/* Trust grid */}
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="trust-grid">
          <TrustCell icon={<Zap size={16} />} label="Fast & Easy" />
          <TrustCell icon={<ShieldCheck size={16} />} label="Secure Transactions" />
          <TrustCell icon={<ThumbsUp size={16} />} label="Reliable Always" />
        </div>

        <div className="mt-5 flex items-center gap-2 text-[11px] text-white/75">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--dg-gold)]" />
          Simple. Secure. Instant. That&apos;s Dolaglobo.
        </div>
      </div>
    </div>
  );
}

function TrustCell({ icon, label }) {
  return (
    <div className="rounded-xl bg-white/8 border border-white/15 px-3 py-3 flex flex-col items-start gap-1.5">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--dg-gold)] text-[#1a1300]">
        {icon}
      </span>
      <span className="text-[11px] leading-tight font-semibold text-white">
        {label}
      </span>
    </div>
  );
}
